import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Validácia environment premenných
if (!supabaseUrl || !supabaseAnonKey) {
  const errorMsg = '❌ CHYBA: Chýbajú Supabase environment premenné!\n\n' +
    'Prosím nastavte v .env súbore alebo ako environment premenné:\n' +
    'VITE_SUPABASE_URL=https://your-project.supabase.co\n' +
    'VITE_SUPABASE_ANON_KEY=your-anon-key\n\n' +
    'Aktuálne hodnoty:\n' +
    `VITE_SUPABASE_URL: ${supabaseUrl ? '✅ nastavené' : '❌ chýba'}\n` +
    `VITE_SUPABASE_ANON_KEY: ${supabaseAnonKey ? '✅ nastavené' : '❌ chýba'}`;
  
  console.error(errorMsg);
  
  // V produkcii zobrazíme user-friendly error
  if (import.meta.env.PROD) {
    console.error('Supabase konfigurácia nie je správne nastavená. Kontaktujte administrátora.');
  }
}

// Vytvorenie Supabase klienta s validáciou
export const supabase = createClient(
  supabaseUrl || '', 
  supabaseAnonKey || '',
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true
    }
  }
);

// Database helpers
export const db = {
  // Tickets
  tickets: {
    getAll: async () => {
      const { data, error } = await supabase
        .from('tickets')
        .select(`
          *,
          order_tickets!order_tickets_ticket_id_fkey (
            order:orders (
              id,
              status
            )
          ),
          ticket_holds (
            session_token,
            expires_at
          )
        `)
        .order('section')
        .order('number_in_section');
      
      if (error) throw error;
      return data ?? null;
    },

    syncHolds: async (sessionToken, ticketNumbers, ttlSeconds = 60) => {
      const sanitizedSessionToken = String(sessionToken || '').trim();
      if (!sanitizedSessionToken) {
        throw new Error('Chýba session token pre rezerváciu lístkov');
      }

      const sanitizedTickets = Array.isArray(ticketNumbers)
        ? ticketNumbers.map((ticket) => String(ticket).trim()).filter(Boolean)
        : [];

      const { data, error } = await supabase.rpc('sync_public_ticket_holds', {
        p_session_token: sanitizedSessionToken,
        p_tickets: sanitizedTickets,
        p_ttl_seconds: ttlSeconds,
      });

      if (error) {
        if (error.message?.includes('held by another customer')) {
          throw new Error('Práve drží iná osoba.');
        }
        if (error.message?.includes('not available')) {
          throw new Error('Niektoré vybrané lístky už nie sú dostupné.');
        }
        throw error;
      }

      return data ?? null;
    },

    releaseHolds: async (sessionToken) => {
      const sanitizedSessionToken = String(sessionToken || '').trim();
      if (!sanitizedSessionToken) return null;

      const { data, error } = await supabase.rpc('release_public_ticket_holds', {
        p_session_token: sanitizedSessionToken,
      });

      if (error) throw error;
      return data ?? null;
    },

    getEligible: async () => {
      const { data, error } = await supabase
        .from('order_tickets')
        .select(`
          id,
          created_at,
          ticket:tickets!order_tickets_ticket_id_fkey (
            id,
            ticket_number,
            section,
            number_in_section,
            status,
            drawn_at
          ),
          order:orders!order_tickets_order_id_fkey (
            id,
            customer_id,
            first_name,
            last_name,
            email,
            status
          )
        `)
        .eq('orders.status', 'paid')
        .eq('ticket.status', 'paid')
        .is('ticket.drawn_at', null);
      
      if (error) throw error;

      return (data ?? []).map((record) => ({
        ...record.ticket,
        order: record.order,
      }));
    }
  },

  // Orders
  orders: {
    create: async (orderData) => {
      // Validácia a sanitizácia vstupov
      const tickets = Array.isArray(orderData.tickets) ? orderData.tickets : [];
      
      if (tickets.length === 0) {
        throw new Error('Musíte vybrať aspoň jeden lístok');
      }
      
      // Uistíme sa, že všetky lístky sú stringy
      const sanitizedTickets = tickets.map(t => String(t).trim()).filter(t => t.length > 0);
      
      if (sanitizedTickets.length === 0) {
        throw new Error('Musíte vybrať aspoň jeden lístok');
      }
      
      const params = {
        p_first_name: String(orderData.first_name || '').trim(),
        p_last_name: String(orderData.last_name || '').trim(),
        p_email: String(orderData.email || '').trim(),
        p_phone: orderData.phone ? String(orderData.phone).trim() : null,
        p_city: orderData.city ? String(orderData.city).trim() : null,
        p_tickets: sanitizedTickets,
        p_session_token: orderData.session_token ? String(orderData.session_token).trim() : null,
      };
      
      // Validácia povinných polí
      if (!params.p_first_name) {
        throw new Error('Meno je povinné');
      }
      if (!params.p_last_name) {
        throw new Error('Priezvisko je povinné');
      }
      if (!params.p_email) {
        throw new Error('Email je povinný');
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(params.p_email)) {
        throw new Error('Neplatný email formát');
      }
      
      console.log('Volám place_public_order_v2 s parametrami:', params);
      
      const { data, error } = await supabase.rpc('place_public_order_v2', params);
      
      if (error) {
        console.error('Chyba pri vytváraní objednávky:', error);
        if (error.message?.includes('Tickets are no longer available')) {
          throw new Error('Niektoré vybrané lístky už nie sú dostupné. Prosím obnovte výber.');
        }
        if (error.message?.includes('Ticket reservation conflict')) {
          throw new Error('Rezervácia sa menila počas objednávky. Skúste to prosím znovu.');
        }
        if (error.message?.includes('Tickets do not exist')) {
          throw new Error('Niektoré vybrané lístky neexistujú. Prosím obnovte stránku.');
        }
        if (error.message?.includes('Ticket hold expired or missing')) {
          throw new Error('Vypršal čas rezervácie lístkov. Skontrolujte výber a skúste znova.');
        }
        throw error;
      }
      
      return data ?? null;
    },

    getAll: async () => {
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          order_tickets!order_tickets_order_id_fkey (
            ticket:tickets!order_tickets_ticket_id_fkey (
              id,
              ticket_number,
              section,
              number_in_section,
              status
            )
          )
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Transformujeme dáta - zachováme pôvodný tickets array (reťazce) a pridáme ticket_objects (objekty)
      return (data ?? []).map(order => ({
        ...order,
        // Zachováme pôvodný tickets array (reťazce ticket_number) z databázy
        tickets: Array.isArray(order.tickets) ? order.tickets : [],
        // Pridáme ticket_objects pre detailné informácie o lístkoch
        ticket_objects: (order.order_tickets || []).map(ot => ot.ticket).filter(Boolean)
      }));
    },

    getFiltered: async ({ search, status }) => {
      let query = supabase
        .from('orders')
        .select(`
          *,
          order_tickets!order_tickets_order_id_fkey (
            ticket:tickets!order_tickets_ticket_id_fkey (
              id,
              ticket_number,
              section,
              number_in_section,
              status
            )
          )
        `)
        .order('created_at', { ascending: false });

      if (status && status !== 'all') {
        query = query.eq('status', status);
      }

      if (search) {
        query = query.or(`first_name.ilike.%${search}%,last_name.ilike.%${search}%,email.ilike.%${search}%,qr_code.ilike.%${search}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      
      // Transformujeme dáta - zachováme pôvodný tickets array (reťazce) a pridáme ticket_objects (objekty)
      return (data ?? []).map(order => ({
        ...order,
        // Zachováme pôvodný tickets array (reťazce ticket_number) z databázy
        tickets: Array.isArray(order.tickets) ? order.tickets : [],
        // Pridáme ticket_objects pre detailné informácie o lístkoch
        ticket_objects: (order.order_tickets || []).map(ot => ot.ticket).filter(Boolean)
      }));
    },

    getRecent: async (limit = 10) => {
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          order_tickets!order_tickets_order_id_fkey (
            ticket:tickets!order_tickets_ticket_id_fkey (
              id,
              ticket_number,
              section,
              number_in_section,
              status
            )
          )
        `)
        .order('created_at', { ascending: false })
        .limit(limit);
      
      if (error) throw error;
      
      // Transformujeme dáta - zachováme pôvodný tickets array (reťazce) a pridáme ticket_objects (objekty)
      return (data ?? []).map(order => ({
        ...order,
        // Zachováme pôvodný tickets array (reťazce ticket_number) z databázy
        tickets: Array.isArray(order.tickets) ? order.tickets : [],
        // Pridáme ticket_objects pre detailné informácie o lístkoch
        ticket_objects: (order.order_tickets || []).map(ot => ot.ticket).filter(Boolean)
      }));
    },

    getStats: async () => {
      const { data, error } = await supabase
        .rpc('get_order_stats');
      
      if (error) throw error;
      return data ?? [];
    },

    approve: async (orderId) => {
      console.log('Schvalujem objednavku s id:', orderId);
      const { data, error } = await supabase
        .from('orders')
        .update({ 
          status: 'paid',
          paid_at: new Date().toISOString()
        })
        .eq('id', orderId)
        .select()
        .single();
      
      if (error) throw error;
      return data ?? null;
    },

    reject: async (orderId) => {
      const { data, error } = await supabase
        .from('orders')
        .delete()
        .eq('id', orderId);
      
      if (error) throw error;
      return data ?? null;
    }
  },

  // Draws
  draws: {
    performDraw: async () => {
      const { data, error } = await supabase.rpc('perform_draw');

      if (error) throw error;

      const draw = Array.isArray(data) ? data[0] : data;
      if (!draw) {
        throw new Error('Draw RPC returned empty result');
      }

      return {
        id: draw.id,
        drawNumber: Number(draw.draw_number ?? draw.drawNumber ?? 0),
        ticketId: draw.ticket_id ?? draw.ticketId ?? null,
        ticketNumber: draw.ticket_number ?? draw.ticketNumber ?? null,
        customerId: draw.customer_id ?? draw.customerId ?? null,
        customerName: draw.customer_name ?? draw.customerName ?? 'Neznámy zákazník',
        orderId: draw.order_id ?? draw.orderId ?? null,
        prizeDescription: draw.prize_description ?? draw.prizeDescription ?? null,
        drawnBy: draw.drawn_by ?? draw.drawnBy ?? 'System',
        drawnAt: draw.drawn_at ?? draw.drawnAt ?? null,
        createdAt: draw.created_at ?? draw.createdAt ?? null,
      };
    },

    performNextGroup: async () => {
      const { data, error } = await supabase.rpc('perform_next_draw_group');
      if (error) throw error;

      const rows = Array.isArray(data) ? data : data ? [data] : [];
      return rows.map((draw) => ({
        id: draw.id,
        drawNumber: Number(draw.draw_number ?? draw.drawNumber ?? 0),
        ticketId: draw.ticket_id ?? draw.ticketId ?? null,
        ticketNumber: draw.ticket_number ?? draw.ticketNumber ?? null,
        customerId: draw.customer_id ?? draw.customerId ?? null,
        customerName: draw.customer_name ?? draw.customerName ?? 'Neznámy zákazník',
        orderId: draw.order_id ?? draw.orderId ?? null,
        prizeDescription: draw.prize_description ?? draw.prizeDescription ?? null,
        drawnBy: draw.drawn_by ?? draw.drawnBy ?? 'System',
        drawnAt: draw.drawn_at ?? draw.drawnAt ?? null,
        createdAt: draw.created_at ?? draw.createdAt ?? null,
      }));
    },

    getHistory: async () => {
      const { data, error } = await supabase.rpc('get_public_draw_history');
      
      if (error) throw error;
      
      // Normalize to camelCase for consistency
      return (data ?? []).map(draw => ({
        id: draw.id ?? null,
        ticketId: draw.ticket_id ?? null,
        ticketNumber: draw.ticket_number ?? null,
        customerId: draw.customer_id ?? null,
        customerName: draw.customer_name ?? 'Neznámy zákazník',
        orderId: draw.order_id ?? null,
        drawNumber: Number(draw.draw_number ?? 0),
        prizeDescription: draw.prize_description ?? null,
        drawnBy: draw.drawn_by ?? null,
        drawnAt: draw.drawn_at ?? null,
        createdAt: draw.created_at ?? null
      }));
    },

    reset: async () => {
      // Delete all draws
      const { error: deleteError } = await supabase
        .from('draws')
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all
      
      if (deleteError) throw deleteError;

      // Reset ticket status
      const { error: resetError } = await supabase
        .from('tickets')
        .update({ 
          status: 'paid',
          drawn_at: null
        })
        .eq('status', 'drawn');

      if (resetError) throw resetError;
      return true;
    }
  },

  // Notifications
  notifications: {
    sendDiscord: async (payload) => {
      const webhookUrl = import.meta.env.VITE_DISCORD_WEBHOOK;
      if (!webhookUrl) return;

      try {
        const response = await fetch(webhookUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            embeds: [{
              title: `Tombola • ${payload.type === 'new_order' ? 'Nová objednávka' : 'Aktualizácia'}`,
              description: `**${payload.order.first_name || payload.order.firstName || ''} ${payload.order.last_name || payload.order.lastName || ''}**\n${payload.order.email}\n\nLístky: ${payload.order.tickets?.join(', ') || ''}\nCelkom: ${payload.order.totalAmount || payload.order.total_amount || payload.order.total || 0}€`,
              color: payload.type === 'new_order' ? 0x00ff00 : 0x0099ff,
              timestamp: new Date().toISOString()
            }]
          })
        });

        if (!response.ok) {
          console.error('Discord webhook failed:', response.statusText);
        }
      } catch (error) {
        console.error('Discord notification error:', error);
      }
    }
  },

  /**
   * Sekcia analytiky a nastavení pre administratívny dashboard.
   */
  analytics: {
    /**
     * Načíta agregované údaje o lístkoch z materializovaného pohľadu/prehľadu.
     * Vracia počty podľa jednotlivých stavov na rýchle zobrazenie.
     */
    getTicketStats: async () => {
      const { data, error } = await supabase
        .from('ticket_stats')
        .select('*')
        .maybeSingle();

      if (error) throw error;
      return data ?? null;
    },

    /**
     * Načíta denný prehľad objednávok a príjmov pre výpočet trendov.
     * @param {number} limit - maximálny počet dní, ktoré chceme načítať.
     */
    getDailyStats: async (limit = 7) => {
      const { data, error } = await supabase
        .from('daily_stats')
        .select('*')
        .order('date', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data ?? [];
    },
  },

  /**
   * Sekcia nastavení podujatia – jedna najnovšia konfigurácia, ktorú zdieľa AdminPage aj Dashboard.
   */
  eventSettings: {
    /**
     * Získa najnovší záznam z tabuľky event_settings.
     * Používame maybeSingle, aby sme vedeli spracovať aj prázdnu tabuľku (vráti null).
     */
    getLatest: async () => {
      const { data, error } = await supabase
        .from('event_settings')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data ?? null;
    },

    /**
     * Aktualizuje nastavenia udalosti podľa ID záznamu.
     * @param {string} id - Primárny kľúč z tabuľky event_settings.
     * @param {object} payload - Nové hodnoty, ktoré sa majú uložiť.
     */
    update: async (id, payload) => {
      const normalizedMaxTickets = Number(payload.max_tickets ?? 0);
      if (normalizedMaxTickets > 1000) {
        throw new Error('Maximálny počet lístkov je 1000 (sekcie A-E, 200 na sekciu).');
      }

      const normalizedPayload = {
        event_name: payload.event_name?.trim() ?? null,
        ticket_price: payload.ticket_price ?? null,
        max_tickets: Number.isFinite(normalizedMaxTickets) ? normalizedMaxTickets : null,
        sales_start: payload.sales_start ?? null,
        sales_end: payload.sales_end ?? null,
      };

      const { data, error } = await supabase
        .from('event_settings')
        .update(normalizedPayload)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data ?? null;
    },
  },
};
